/**
 * 地点配置编辑器组件
 * 提供地点配置的表单式编辑界面
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Tabs,
  Row,
  Col,
  Typography,
  Alert,
} from 'antd'
import {
  EnvironmentOutlined,
  HomeOutlined,
} from '@ant-design/icons'
import type { LocationConfigData } from '@/types/scriptConfigBlock'

const { TextArea } = Input
const { Text } = Typography

// 地点类型选项
const LOCATION_TYPE_OPTIONS = [
  { value: 'indoor', label: '室内' },
  { value: 'outdoor', label: '室外' },
  { value: 'virtual', label: '虚拟空间' },
]

// 地点编辑器属性
interface LocationEditorFormProps {
  initialData?: LocationConfigData
  onSave: (data: LocationConfigData) => Promise<void>
  loading?: boolean
}

// 主组件
const LocationEditorForm: React.FC<LocationEditorFormProps> = ({
  initialData,
  onSave,
  loading = false
}) => {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('basic')
  const [saving, setSaving] = useState(false)
  const [availableDevices, setAvailableDevices] = useState<string[]>([])

  // 初始化表单数据
  useEffect(() => {
    if (initialData) {
      // 处理 connections 数组：确保元素是字符串再 join
      const normalizeConnections = (connections: any): string => {
        if (!Array.isArray(connections)) return ''
        return connections.map(c => String(c)).join(', ')
      }

      // 处理 properties 对象：转换为 JSON 字符串
      const normalizeProperties = (properties: any): string => {
        if (!properties) return '{}'
        if (typeof properties === 'object') {
          return JSON.stringify(properties, null, 2)
        }
        return String(properties)
      }

      form.setFieldsValue({
        key: initialData.key,
        name: initialData.name,
        description: initialData.description,
        location_type: initialData.location_type,
        connections: normalizeConnections(initialData.connections),
        atmosphere: initialData.atmosphere,
        properties: normalizeProperties(initialData.properties),
        // properties 的嵌套字段（rooms, objects）
        rooms: typeof initialData.properties?.rooms === 'object'
          ? JSON.stringify(initialData.properties.rooms, null, 2)
          : '{}',
        objects: typeof initialData.properties?.objects === 'object'
          ? JSON.stringify(initialData.properties.objects, null, 2)
          : '[]',
        bounds: typeof initialData.properties?.bounds === 'object'
          ? JSON.stringify(initialData.properties.bounds, null, 2)
          : '{}',
      })
      setAvailableDevices(initialData.available_devices || [])
    } else {
      form.resetFields()
      setAvailableDevices([])
    }
  }, [initialData, form])

  // 保存处理
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      // 解析 properties JSON 字符串
      const parseJsonField = (value: any, defaultValue: any = {}): any => {
        if (!value) return defaultValue
        if (typeof value === 'string' && value.trim().startsWith('{')) {
          try {
            return JSON.parse(value)
          } catch {
            return defaultValue
          }
        }
        return value
      }

      const rooms = parseJsonField(values.rooms, {})
      const objects = parseJsonField(values.objects, [])
      const bounds = parseJsonField(values.bounds, {})
      const properties = parseJsonField(values.properties, {})

      const locationData: LocationConfigData = {
        key: values.key,
        name: values.name,
        description: values.description,
        location_type: values.location_type,
        connections: values.connections ? values.connections.split(',').map((c: string) => c.trim()) : [],
        atmosphere: values.atmosphere,
        available_devices: availableDevices,
        properties: {
          ...properties,
          rooms,
          objects,
          bounds,
        },
      }

      await onSave(locationData)
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      title={
        <Space>
          <EnvironmentOutlined style={{ color: '#13c2c2' }} />
          <span>{initialData?.name || '新建地点'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<HomeOutlined />}
          onClick={handleSave}
          loading={saving || loading}
        >
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="地点键" rules={[{ required: true }]}>
                  <Input placeholder="例如: loc_hall" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="地点名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 兵站大厅" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="location_type" label="地点类型" rules={[{ required: true }]}>
                  <Select placeholder="选择地点类型" options={LOCATION_TYPE_OPTIONS} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="描述">
              <TextArea rows={3} placeholder="地点的详细描述..." />
            </Form.Item>

            <Form.Item name="atmosphere" label="氛围">
              <Input placeholder="例如: 热闹但暗流涌动" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="connections" label="连接地点 (逗号分隔)">
                  <Input placeholder="loc_banquet_hall, loc_medical_room" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="可用设备">
                  <Select
                    mode="tags"
                    value={availableDevices}
                    onChange={setAvailableDevices}
                    placeholder="设备类型"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="区域配置" key="rooms">
            <Alert
              message="区域配置说明"
              description="地点内的子区域配置，包括房间名称、中心位置、尺寸、描述等。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text type="secondary">
                区域数据以 JSON 格式存储在 properties.rooms 字段中。
                示例格式：
              </Text>
              <pre style={{ fontSize: 11, marginTop: 8 }}>{'{"大厅中央": {"center": {"x": 400, "y": 300}, "size": {"x": 600, "y": 400}}}'}</pre>
            </Card>
            <Form.Item name="rooms" label="区域配置 (JSON)">
              <TextArea
                rows={8}
                placeholder='{"大厅中央": {"center": {"x": 400, "y": 300}, "size": {"x": 600, "y": 400}, "description": "描述"}}'
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="交互对象" key="objects">
            <Alert
              message="交互对象配置说明"
              description="地点内的可交互对象，包括家具、物品、传送点等。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text type="secondary">
                交互对象数据以 JSON 格式存储在 properties.objects 字段中。
                每个对象包含 id, name, position, type, description 等字段。
              </Text>
            </Card>
            <Form.Item name="objects" label="交互对象 (JSON)">
              <TextArea
                rows={10}
                placeholder={`[
  {
    "id": "obj_piano",
    "name": "钢琴",
    "position": {"x": 600, "y": 300},
    "type": "interactive",
    "description": "一架三角钢琴"
  }
]`}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="扩展属性" key="properties">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="bounds" label="边界范围 (JSON)">
                  <TextArea
                    rows={3}
                    placeholder='{"x": 800, "y": 600}'
                    style={{ fontFamily: 'monospace' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="properties" label="其他属性 (JSON)">
                  <TextArea
                    rows={3}
                    placeholder='{"capacity": 30, "facilities": ["桌椅", "音响"]}'
                    style={{ fontFamily: 'monospace' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

const { TabPane } = Tabs

export default LocationEditorForm
